import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import type { NotificationMessage } from '@/core/notification/notification-message.model';
import type { OpportunityStatus } from '@/core/opportunity/opportunity-status';
import type { BannerType } from '@/design-system/molecules/banner/banner.component';
import { ToastStore } from '@/features/toast-store.service';

export interface OpportunityLeftPendingStatusEvent {
  readonly opportunityId: string;
  readonly receivedAt: number;
}

const STATUS_TOAST_TYPE: Partial<Record<OpportunityStatus, BannerType>> = {
  LOAD_CURVE_RETRIEVED: 'SUCCESS',
  LOAD_CURVE_RETRIEVAL_ERROR: 'ERROR',
  CONTRACT_SIGNED: 'SUCCESS',
  OFFER_EXPIRED: 'ERROR',
  CONTRACT_EXPIRED: 'ERROR',
};

const DEFAULT_TOAST_TYPE: BannerType = 'WARN';

const STATUS_TOAST_TITLE_KEY: Partial<Record<OpportunityStatus, string>> = {
  LOAD_CURVE_RETRIEVED: 'notifications.opportunityStatusChanged.loadCurveRetrieved.title',
  LOAD_CURVE_RETRIEVAL_ERROR: 'notifications.opportunityStatusChanged.loadCurveRetrievalError.title',
  CONTRACT_SIGNED: 'notifications.opportunityStatusChanged.contractSigned.title',
  OFFER_EXPIRED: 'notifications.opportunityStatusChanged.offerExpired.title',
  CONTRACT_EXPIRED: 'notifications.opportunityStatusChanged.contractExpired.title',
};

const DEFAULT_TOAST_TITLE_KEY = 'notifications.opportunityStatusChanged.default.title';

@Injectable({ providedIn: 'root' })
export class RealtimeNotificationStore {
  private readonly toastStore = inject(ToastStore);

  private readonly _opportunityLeftPendingStatus = signal<OpportunityLeftPendingStatusEvent | null>(null);

  public readonly opportunityLeftPendingStatus = this._opportunityLeftPendingStatus.asReadonly();

  handleOpportunityLeftPendingStatus(message: NotificationMessage): void {
    this._opportunityLeftPendingStatus.set({ opportunityId: message.opportunityId, receivedAt: Date.now() });

    this.toastStore.show({
      type: STATUS_TOAST_TYPE[message.status] ?? DEFAULT_TOAST_TYPE,
      title: STATUS_TOAST_TITLE_KEY[message.status] ?? DEFAULT_TOAST_TITLE_KEY,
      titleParams: { legalName: message.legalName },
    });
  }
}

export function onOpportunityLeftPendingStatus(handler: (event: OpportunityLeftPendingStatusEvent) => void): void {
  const realtimeNotificationStore = inject(RealtimeNotificationStore);
  const eventBeforeSubscription = untracked(() => realtimeNotificationStore.opportunityLeftPendingStatus());

  effect(() => {
    const event = realtimeNotificationStore.opportunityLeftPendingStatus();
    if (event === null || event === eventBeforeSubscription) return;
    untracked(() => {
      handler(event);
    });
  });
}
